import React, { memo, useState } from "react";
import { Box, Flex, Text } from "@chakra-ui/react";
import { ArrowsPointingOutIcon } from "@heroicons/react/24/outline";
import { a } from "../appColors";

/**
 * A figure on the paper, with a way into it (Figma 269:109).
 *
 * Hovering dims the image and offers one control in the corner. The figure
 * itself is untouched underneath — the scrim is a sibling, not a filter, so
 * nothing about the printed diagram changes.
 */
const FigureFrame = ({ label = "figure", onExpand, children }) => {
  const [hovered, setHovered] = useState(false);

  return (
    <Box
      position="relative"
      width="fit-content"
      maxW="100%"
      borderRadius="14px"
      overflow="hidden"
      border="1px solid"
      borderColor={a.edge}
      onPointerEnter={() => setHovered(true)}
      onPointerLeave={() => setHovered(false)}
    >
      {children}

      {onExpand && (
        <>
          <Box
            position="absolute"
            inset="0"
            bg="rgba(34,34,34,0.38)"
            opacity={hovered ? 1 : 0}
            transition="opacity 0.16s"
            pointerEvents="none"
          />
          <Flex
            as="button"
            type="button"
            aria-label={`Open ${label} to draw on it`}
            align="center"
            gap="6px"
            position="absolute"
            top="10px"
            right="10px"
            px="10px"
            h="28px"
            borderRadius="200px"
            bg="rgba(20,20,20,0.82)"
            color="#f5f5f5"
            fontSize="11px"
            backdropFilter="blur(6px)"
            opacity={hovered ? 1 : 0}
            transform={hovered ? "translateY(0)" : "translateY(-4px)"}
            transition="opacity 0.16s, transform 0.16s"
            // Hidden from the pointer until it is shown, so an invisible
            // button can never swallow a click meant for the paper.
            pointerEvents={hovered ? "auto" : "none"}
            cursor="pointer"
            _hover={{ bg: "rgba(20,20,20,0.95)" }}
            _focusVisible={{ opacity: 1, pointerEvents: "auto", outline: "2px solid #f5f5f5" }}
            onClick={onExpand}
          >
            <ArrowsPointingOutIcon style={{ width: 14, height: 14 }} />
            <Text as="span">Open</Text>
          </Flex>
        </>
      )}
    </Box>
  );
};

export default memo(FigureFrame);
